import React, { useMemo, useState } from 'react';
import {
  RefreshCw,
  Loader2,
  DollarSign,
  Activity,
  AlertTriangle,
  Coins,
  Timer,
  Play,
  Plus,
  Trash2,
  Pencil,
  X,
  ShieldCheck,
  Boxes,
  BarChart3,
  Server,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { BrainShell } from '@/client/pages/Admin/Brain/BrainSegments';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  Backend,
  BackendHealth,
  Budget,
  BudgetUpsertPayload,
  EnablementModel,
  EnablementProvider,
  UsageMetrics,
  UsageWindow,
} from '@/client/types/ai-management';
import { useOverviewQuery } from '@/client/api/ai-management/overview.query';
import { useTimeseriesQuery } from '@/client/api/ai-management/timeseries.query';
import { useErrorsQuery } from '@/client/api/ai-management/errors.query';
import { useBudgetsQuery } from '@/client/api/ai-management/budgets.query';
import { useUpsertBudgetMutation } from '@/client/api/ai-management/budget-upsert.mutation';
import { useDeleteBudgetMutation } from '@/client/api/ai-management/budget-delete.mutation';
import { useBackendsQuery } from '@/client/api/ai-management/backends.query';
import { useEnablementQuery } from '@/client/api/ai-management/enablement.query';
import { useSetEnablementMutation } from '@/client/api/ai-management/enablement-set.mutation';
import { useRunTestMutation } from '@/client/api/ai-management/test.mutation';

// ─── Formatting helpers ──────────────────────────────────────────────────────

const fmtUsd = (n = 0) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: n < 1 ? 4 : 2 })}`;
const fmtNum = (n = 0) => n.toLocaleString();
const fmtPct = (n = 0) => `${(n * 100).toFixed(1)}%`;
const fmtLatency = (n = 0) => `${Math.round(n)}ms`;

const WINDOWS: { label: string; hours: UsageWindow }[] = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

type Tab = 'overview' | 'enablement' | 'budgets' | 'servers' | 'test';

// ─── Small shared UI ─────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="truncate text-xl font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const Toggle: React.FC<{
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ on, disabled, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={onClick}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
      on ? 'bg-green-500' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        on ? 'translate-x-5' : 'translate-x-1'
      }`}
    />
  </button>
);

// Column set shared by the by_feature / by_model / by_provider tables.
const MetricTable: React.FC<{
  title: string;
  keyLabel: string;
  keyField: 'feature_id' | 'model_id' | 'provider';
  rows: (UsageMetrics & Record<string, any>)[];
}> = ({ title, keyLabel, keyField, rows }) => (
  <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
    <div className="border-b border-gray-100 px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
            <th className="px-4 py-2 font-medium">{keyLabel}</th>
            <th className="px-4 py-2 text-right font-medium">Cost</th>
            <th className="px-4 py-2 text-right font-medium">Calls</th>
            <th className="px-4 py-2 text-right font-medium">Err rate</th>
            <th className="px-4 py-2 text-right font-medium">Tokens</th>
            <th className="px-4 py-2 text-right font-medium">Avg lat.</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                No data for this window
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={String(r[keyField])} className="border-b border-gray-50 last:border-0">
              <td className="px-4 py-2 font-medium text-gray-800">{String(r[keyField])}</td>
              <td className="px-4 py-2 text-right text-gray-700">{fmtUsd(r.cost_usd)}</td>
              <td className="px-4 py-2 text-right text-gray-700">{fmtNum(r.calls)}</td>
              <td className="px-4 py-2 text-right">
                <span className={r.error_rate > 0.05 ? 'text-red-600' : 'text-gray-700'}>
                  {fmtPct(r.error_rate)}
                </span>
              </td>
              <td className="px-4 py-2 text-right text-gray-700">
                {fmtNum((r.input_tokens ?? 0) + (r.output_tokens ?? 0))}
              </td>
              <td className="px-4 py-2 text-right text-gray-700">{fmtLatency(r.avg_latency_ms)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Overview tab ────────────────────────────────────────────────────────────

const OverviewTab: React.FC<{ sinceHours: UsageWindow }> = ({ sinceHours }) => {
  const overview = useOverviewQuery(sinceHours);
  const timeseries = useTimeseriesQuery({ since_hours: sinceHours, bucket_hours: 24 });
  const errorsQuery = useErrorsQuery({ limit: 25 });

  const totals = overview.data?.totals;
  const series = timeseries.data?.series ?? [];
  const maxCost = useMemo(() => Math.max(0, ...series.map((s) => s.cost_usd)), [series]);

  if (overview.isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading usage…
      </div>
    );
  }

  if (overview.isError) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
        {getApiErrorMessage(overview.error, 'Failed to load usage overview')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Total Cost"
          value={fmtUsd(totals?.cost_usd)}
          icon={<DollarSign className="h-5 w-5 text-emerald-600" />}
          color="bg-emerald-50"
        />
        <StatCard
          label="Calls"
          value={fmtNum(totals?.calls)}
          icon={<Activity className="h-5 w-5 text-blue-600" />}
          color="bg-blue-50"
        />
        <StatCard
          label="Error Rate"
          value={fmtPct(totals?.error_rate)}
          icon={<AlertTriangle className="h-5 w-5 text-amber-600" />}
          color="bg-amber-50"
        />
        <StatCard
          label="Total Tokens"
          value={fmtNum(totals?.total_tokens)}
          icon={<Coins className="h-5 w-5 text-purple-600" />}
          color="bg-purple-50"
        />
        <StatCard
          label="Avg Latency"
          value={fmtLatency(totals?.avg_latency_ms)}
          icon={<Timer className="h-5 w-5 text-cyan-600" />}
          color="bg-cyan-50"
        />
      </div>

      {/* Daily cost chart (inline bars, no chart dep) */}
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Daily Cost (USD)</h3>
        </div>
        {series.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">No timeseries data</p>
        ) : (
          <div className="flex items-end gap-2" style={{ height: 160 }}>
            {series.map((pt) => {
              const pct = maxCost > 0 ? (pt.cost_usd / maxCost) * 100 : 0;
              const day = pt.bucket?.slice(5, 10) ?? '';
              return (
                <div key={pt.bucket} className="flex flex-1 flex-col items-center justify-end gap-1">
                  <span className="text-[10px] text-gray-500">{fmtUsd(pt.cost_usd)}</span>
                  <div
                    className="w-full rounded-t bg-emerald-400 transition-all hover:bg-emerald-500"
                    style={{ height: `${Math.max(pct, 2)}%` }}
                    title={`${pt.bucket}: ${fmtUsd(pt.cost_usd)} · ${fmtNum(pt.calls)} calls`}
                  />
                  <span className="text-[10px] text-gray-400">{day}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Breakdown tables */}
      <MetricTable
        title="By Feature"
        keyLabel="Feature"
        keyField="feature_id"
        rows={overview.data?.by_feature ?? []}
      />
      <MetricTable
        title="By Model"
        keyLabel="Model"
        keyField="model_id"
        rows={overview.data?.by_model ?? []}
      />
      <MetricTable
        title="By Provider"
        keyLabel="Provider"
        keyField="provider"
        rows={overview.data?.by_provider ?? []}
      />

      {/* Recent errors */}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Recent Errors</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {(errorsQuery.data?.errors ?? []).length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-400">No recent errors 🎉</p>
          )}
          {(errorsQuery.data?.errors ?? []).map((e, i) => (
            <div key={e.request_id || i} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span>{e.ts}</span>
                <Badge variant="secondary">{e.provider}</Badge>
                <span className="text-gray-400">{e.model_id}</span>
                <span className="text-gray-400">·</span>
                <span>{e.feature_id}</span>
                <Badge variant="destructive">{e.error_type}</Badge>
              </div>
              <p className="mt-1 break-words text-sm text-gray-800">{e.error_message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Enablement tab ──────────────────────────────────────────────────────────

const EnablementTab: React.FC = () => {
  const { data, isLoading, isError, error } = useEnablementQuery();
  const setEnablement = useSetEnablementMutation();

  const [pending, setPending] = useState<string | null>(null);

  const applyToggle = async (scope: 'provider' | 'model', key: string, next: boolean) => {
    setPending(`${scope}:${key}`);
    try {
      await setEnablement.mutateAsync({ scope, key, enabled: next });
      toast.success(`${scope === 'provider' ? 'Provider' : 'Model'} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update enablement'));
    } finally {
      setPending(null);
    }
  };

  const clearOverride = async (scope: 'provider' | 'model', key: string) => {
    setPending(`${scope}:${key}`);
    try {
      await setEnablement.mutateAsync({ scope, key, clear: true });
      toast.success('Override cleared — reverted to registry default');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to clear override'));
    } finally {
      setPending(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading enablement…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
        {getApiErrorMessage(error, 'Failed to load enablement')}
      </div>
    );
  }

  const renderProviderRow = (p: EnablementProvider) => {
    const id = `provider:${p.provider}`;
    return (
      <div key={id} className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-800">{p.provider}</span>
            {p.override ? (
              <Badge variant="default">override</Badge>
            ) : (
              <Badge variant="secondary">registry</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {p.servers_enabled}/{p.servers} servers enabled
          </p>
        </div>
        <div className="flex items-center gap-3">
          {p.override && (
            <button
              type="button"
              onClick={() => clearOverride('provider', p.provider)}
              className="text-xs text-gray-400 underline hover:text-gray-600"
            >
              clear
            </button>
          )}
          {pending === id ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : (
            <Toggle
              on={p.effective_enabled}
              onClick={() => applyToggle('provider', p.provider, !p.effective_enabled)}
            />
          )}
        </div>
      </div>
    );
  };

  const renderModelRow = (m: EnablementModel) => {
    const id = `model:${m.model_id}`;
    return (
      <div key={id} className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-gray-800">{m.model_name}</span>
            {m.override ? (
              <Badge variant="default">override</Badge>
            ) : (
              <Badge variant="secondary">registry</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {m.provider} · {m.model_id}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {m.override && (
            <button
              type="button"
              onClick={() => clearOverride('model', m.model_id)}
              className="text-xs text-gray-400 underline hover:text-gray-600"
            >
              clear
            </button>
          )}
          {pending === id ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : (
            <Toggle
              on={m.effective_enabled}
              onClick={() => applyToggle('model', m.model_id, !m.effective_enabled)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
          <ShieldCheck className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Providers</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {(data?.providers ?? []).map(renderProviderRow)}
          {(data?.providers ?? []).length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-400">No providers</p>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
          <Boxes className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Models</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {(data?.models ?? []).map(renderModelRow)}
          {(data?.models ?? []).length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-400">No models</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Budgets tab ─────────────────────────────────────────────────────────────

interface BudgetForm {
  budget_id?: string;
  name: string;
  scope: 'feature' | 'tenant';
  scopeValue: string;
  monthly_limit_usd: string;
  daily_limit_usd: string;
  alert_threshold_percent: string;
  alert_email: string;
  enabled: boolean;
}

const emptyForm: BudgetForm = {
  name: '',
  scope: 'feature',
  scopeValue: '',
  monthly_limit_usd: '',
  daily_limit_usd: '',
  alert_threshold_percent: '',
  alert_email: '',
  enabled: true,
};

const budgetToForm = (b: Budget): BudgetForm => ({
  budget_id: b.budget_id,
  name: b.name,
  scope: b.tenant_id ? 'tenant' : 'feature',
  scopeValue: b.tenant_id ?? b.feature_id ?? '',
  monthly_limit_usd: String(b.monthly_limit_usd ?? ''),
  daily_limit_usd: b.daily_limit_usd != null ? String(b.daily_limit_usd) : '',
  alert_threshold_percent:
    b.alert_threshold_percent != null ? String(b.alert_threshold_percent) : '',
  alert_email: b.alert_email ?? '',
  enabled: b.enabled,
});

const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500';
const labelCls = 'mb-1 block text-sm font-medium text-gray-700';

const BudgetsTab: React.FC = () => {
  const { data, isLoading, isError, error } = useBudgetsQuery();
  const upsert = useUpsertBudgetMutation();
  const del = useDeleteBudgetMutation();

  const [form, setForm] = useState<BudgetForm | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => setForm({ ...emptyForm });
  const openEdit = (b: Budget) => setForm(budgetToForm(b));
  const closeForm = () => setForm(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const monthly = Number(form.monthly_limit_usd);
    if (!form.name.trim()) return toast.error('Name is required');
    if (!Number.isFinite(monthly) || monthly <= 0)
      return toast.error('Monthly limit must be a positive number');

    const payload: BudgetUpsertPayload = {
      budget_id: form.budget_id,
      name: form.name.trim(),
      monthly_limit_usd: monthly,
      enabled: form.enabled,
      // Scope: exactly one of feature_id / tenant_id, null the other.
      feature_id: form.scope === 'feature' ? form.scopeValue.trim() || null : null,
      tenant_id: form.scope === 'tenant' ? form.scopeValue.trim() || null : null,
      daily_limit_usd: form.daily_limit_usd ? Number(form.daily_limit_usd) : null,
      alert_threshold_percent: form.alert_threshold_percent
        ? Number(form.alert_threshold_percent)
        : null,
      alert_email: form.alert_email.trim() || null,
    };

    try {
      await upsert.mutateAsync(payload);
      toast.success(form.budget_id ? 'Budget updated' : 'Budget created');
      closeForm();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save budget'));
    }
  };

  const remove = async (b: Budget) => {
    setDeletingId(b.budget_id);
    try {
      await del.mutateAsync(b.budget_id);
      toast.success('Budget deleted');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete budget'));
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading budgets…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
        {getApiErrorMessage(error, 'Failed to load budgets')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> New Budget
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Scope</th>
                <th className="px-4 py-2 text-right font-medium">Monthly</th>
                <th className="px-4 py-2 text-right font-medium">Spent</th>
                <th className="px-4 py-2 font-medium">Month %</th>
                <th className="px-4 py-2 font-medium">State</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.budgets ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                    No budgets configured
                  </td>
                </tr>
              )}
              {(data?.budgets ?? []).map((b) => {
                const pct = Math.round((b.month_pct ?? 0) * 100);
                const barColor = b.exceeded
                  ? 'bg-red-500'
                  : b.alerting
                    ? 'bg-amber-500'
                    : 'bg-emerald-500';
                return (
                  <tr key={b.budget_id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-2">
                      <span className="font-medium text-gray-800">{b.name}</span>
                      {!b.enabled && (
                        <Badge variant="secondary" className="ml-2">
                          disabled
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {b.tenant_id
                        ? `tenant: ${b.tenant_id}`
                        : b.feature_id
                          ? `feature: ${b.feature_id}`
                          : 'global'}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">
                      {fmtUsd(b.monthly_limit_usd)}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">
                      {fmtUsd(b.current_month_spent)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={`h-full ${barColor}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {b.exceeded ? (
                        <Badge variant="destructive">exceeded</Badge>
                      ) : b.alerting ? (
                        <Badge variant="default" className="bg-amber-500 hover:bg-amber-500">
                          alerting
                        </Badge>
                      ) : (
                        <Badge variant="secondary">ok</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(b)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(b)}
                          disabled={deletingId === b.budget_id}
                          className="rounded p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === b.budget_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / edit form */}
      {form && (
        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-800">
              {form.budget_id ? 'Edit Budget' : 'New Budget'}
            </h3>
            <button
              type="button"
              onClick={closeForm}
              className="rounded p-1 text-gray-400 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className={labelCls}>Name</label>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Search team monthly cap"
              />
            </div>
            <div>
              <label className={labelCls}>Scope</label>
              <select
                className={inputCls}
                value={form.scope}
                onChange={(e) =>
                  setForm({ ...form, scope: e.target.value as 'feature' | 'tenant' })
                }
              >
                <option value="feature">Feature</option>
                <option value="tenant">Tenant</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>
                {form.scope === 'feature' ? 'Feature ID' : 'Tenant ID'} (optional → global)
              </label>
              <input
                className={inputCls}
                value={form.scopeValue}
                onChange={(e) => setForm({ ...form, scopeValue: e.target.value })}
                placeholder={form.scope === 'feature' ? 'search-query-parse' : 'tenant-123'}
              />
            </div>
            <div>
              <label className={labelCls}>Monthly Limit (USD)</label>
              <input
                className={inputCls}
                type="number"
                min={0}
                step="0.01"
                value={form.monthly_limit_usd}
                onChange={(e) => setForm({ ...form, monthly_limit_usd: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Daily Limit (USD, optional)</label>
              <input
                className={inputCls}
                type="number"
                min={0}
                step="0.01"
                value={form.daily_limit_usd}
                onChange={(e) => setForm({ ...form, daily_limit_usd: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Alert Threshold (%)</label>
              <input
                className={inputCls}
                type="number"
                min={0}
                max={100}
                value={form.alert_threshold_percent}
                onChange={(e) => setForm({ ...form, alert_threshold_percent: e.target.value })}
                placeholder="80"
              />
            </div>
            <div>
              <label className={labelCls}>Alert Email (optional)</label>
              <input
                className={inputCls}
                type="email"
                value={form.alert_email}
                onChange={(e) => setForm({ ...form, alert_email: e.target.value })}
                placeholder="ops@feelyfeely.cloud"
              />
            </div>
            <div className="flex items-center gap-3 md:col-span-2">
              <Toggle on={form.enabled} onClick={() => setForm({ ...form, enabled: !form.enabled })} />
              <span className="text-sm text-gray-700">Enabled</span>
            </div>
            <div className="flex justify-end gap-2 md:col-span-2">
              <Button type="button" variant="outline" onClick={closeForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending} className="gap-2">
                {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {form.budget_id ? 'Save' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ─── Test tab ────────────────────────────────────────────────────────────────

const TestTab: React.FC = () => {
  const enablement = useEnablementQuery();
  const runTest = useRunTestMutation();

  const providers = useMemo(
    () => (enablement.data?.providers ?? []).map((p) => p.provider),
    [enablement.data],
  );

  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [prompt, setPrompt] = useState('Explain machine learning in 3 sentences.');
  const [maxTokens, setMaxTokens] = useState('150');

  // Default provider once enablement loads.
  React.useEffect(() => {
    if (!provider && providers.length) setProvider(providers[0]);
  }, [providers, provider]);

  const run = async () => {
    if (!provider) return toast.error('Select a provider');
    if (!prompt.trim()) return toast.error('Enter a prompt');
    try {
      await runTest.mutateAsync({
        provider,
        model: model.trim() || undefined,
        prompt,
        max_tokens: maxTokens ? Number(maxTokens) : undefined,
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Test call failed'));
    }
  };

  const result = runTest.data;
  const m = result?.metrics ?? {};

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-gray-800">Run a test completion</h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Provider</label>
            <select
              className={inputCls}
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {providers.length === 0 && <option value="">No providers available</option>}
              {providers.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Model (optional — defaults server-side)</label>
            <input
              className={inputCls}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="leave blank for provider default"
            />
          </div>
          <div>
            <label className={labelCls}>Prompt</label>
            <textarea
              className={inputCls}
              rows={5}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>
          <div className="w-40">
            <label className={labelCls}>Max Tokens</label>
            <input
              className={inputCls}
              type="number"
              min={1}
              max={4096}
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
            />
          </div>
          <Button onClick={run} disabled={runTest.isPending} className="w-full gap-2">
            {runTest.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Run test
          </Button>
          <p className="text-xs text-gray-400">
            Credentials are injected server-side — no API key is ever entered or sent from the
            browser.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-gray-800">Result</h3>
        {runTest.isPending && (
          <div className="flex items-center gap-2 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Running…
          </div>
        )}
        {!runTest.isPending && !result && (
          <p className="text-sm text-gray-400">Run a test to see the response.</p>
        )}
        {result && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{result.provider}</Badge>
              <Badge variant="secondary">{result.model}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="rounded-lg bg-gray-50 p-2">
                <p className="text-xs text-gray-500">Tokens</p>
                <p className="font-semibold text-gray-800">
                  {fmtNum(
                    (m.total_tokens as number) ??
                      ((m.input_tokens as number) ?? 0) + ((m.output_tokens as number) ?? 0),
                  )}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <p className="text-xs text-gray-500">Cost est.</p>
                <p className="font-semibold text-gray-800">{fmtUsd(m.cost_estimate as number)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <p className="text-xs text-gray-500">Latency</p>
                <p className="font-semibold text-gray-800">{fmtLatency(m.latency_ms as number)}</p>
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-1 text-xs text-gray-500">Content</p>
              <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
                {result.content}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Servers tab ─────────────────────────────────────────────────────────────

const HEALTH_STYLES: Record<BackendHealth, { dot: string; label: string; text: string }> = {
  healthy: { dot: 'bg-green-500', label: 'Healthy', text: 'text-green-700' },
  degraded: { dot: 'bg-amber-500', label: 'Degraded', text: 'text-amber-700' },
  unhealthy: { dot: 'bg-red-500', label: 'Unhealthy', text: 'text-red-700' },
  unknown: { dot: 'bg-gray-300', label: 'Unknown', text: 'text-gray-500' },
};

const fmtSince = (iso: string | null) => {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
};

const HealthCell: React.FC<{ backend: Backend }> = ({ backend }) => {
  // A hosted provider carries the registry file's default status, not a
  // measurement. Showing it green would claim a check we never ran.
  if (!backend.probed) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-gray-400">
        <span className="h-2 w-2 rounded-full bg-gray-200" />
        Not monitored
      </span>
    );
  }
  const style = HEALTH_STYLES[backend.health_status] ?? HEALTH_STYLES.unknown;
  return (
    <span className={`inline-flex items-center gap-2 text-sm font-medium ${style.text}`}>
      <span className={`h-2 w-2 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
};

const ServersTab: React.FC = () => {
  const { data, isLoading, isError, error } = useBackendsQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading backends…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
        {getApiErrorMessage(error, 'Failed to load backend fleet')}
      </div>
    );
  }

  const backends = data?.backends ?? [];
  const excluded = data?.excluded ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2">
          <Server className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{data?.in_pool ?? 0}</span> of{' '}
            {data?.total ?? 0} in the routing pool
          </span>
        </div>
        {excluded.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            Excluded: {excluded.join(', ')}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Backend</th>
              <th className="px-4 py-3">Provider</th>
              <th className="px-4 py-3">Health</th>
              <th className="px-4 py-3">Last check</th>
              <th className="px-4 py-3 text-right">Models</th>
              <th className="px-4 py-3 text-right">Weight</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {backends.map((b) => (
              <tr key={b.server_id} className={b.enabled ? '' : 'opacity-50'}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{b.server_id}</div>
                  <div className="break-all text-xs text-gray-400">{b.endpoint_url}</div>
                </td>
                <td className="px-4 py-3 text-gray-600">{b.provider}</td>
                <td className="px-4 py-3"><HealthCell backend={b} /></td>
                <td className="px-4 py-3 text-gray-500">
                  {b.probed ? fmtSince(b.last_health_check) : '—'}
                </td>
                <td className="px-4 py-3 text-right text-gray-600">{b.supported_models}</td>
                <td className="px-4 py-3 text-right text-gray-600">{b.priority_weight}</td>
              </tr>
            ))}
            {backends.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  No backends registered.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Self-hosted backends are probed on their own interval. Hosted providers are not
        polled — a probe there spends quota without telling us anything the circuit
        breaker does not already handle.
      </p>
    </div>
  );
};

// ─── Page shell ──────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'enablement', label: 'Enablement' },
  { id: 'budgets', label: 'Budgets' },
  { id: 'servers', label: 'Servers' },
  { id: 'test', label: 'Test' },
];

const AIManagementPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Brain', href: '/admin/ai' },
    { label: 'Controls' },
  ]);

  const [tab, setTab] = useState<Tab>('overview');
  const [windowHours, setWindowHours] = useState<UsageWindow>(24);

  return (
    <BrainShell
      title="Controls"
      description="LLM usage, cost, enablement, budgets, and testing."
      actions={
        tab === 'overview' ? (
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.hours}
                type="button"
                onClick={() => setWindowHours(w.hours)}
                className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  windowHours === w.hours
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {w.label === '24h' && <RefreshCw className="h-3.5 w-3.5" />}
                {w.label}
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="space-y-6">
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab sinceHours={windowHours} />}
        {tab === 'enablement' && <EnablementTab />}
        {tab === 'budgets' && <BudgetsTab />}
        {tab === 'servers' && <ServersTab />}
        {tab === 'test' && <TestTab />}
      </div>
    </BrainShell>
  );
};

export default AIManagementPage;
